import React, { useEffect, useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { resolveAssetPath } from '../../config/basePath';

/** Data CHANGELOG YYYY-MM-DD → testo leggibile (it-IT), mezzogiorno UTC per evitare shift fusi */
function formatReleaseDate(ymd) {
  if (!ymd || typeof ymd !== 'string') return null;
  const m = ymd.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return ymd;
  const d = new Date(`${m[1]}-${m[2]}-${m[3]}T12:00:00.000Z`);
  if (Number.isNaN(d.getTime())) return ymd;
  return d.toLocaleDateString('it-IT', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
}

const VersionChecker = ({ children }) => {
  const [needsRefresh, setNeedsRefresh] = useState(false);
  const [versionData, setVersionData] = useState(null);
  const { isDark } = useTheme();
  
  // Determina se siamo in ambiente di sviluppo
  const isDevelopment = process.env.NODE_ENV === 'development';

  useEffect(() => {
    // In fase di sviluppo, il check è opzionale
    if (isDevelopment) {
      console.log('Version checking disabled in development mode');
      return;
    }
    
    const checkVersion = async () => {
      try {
        // Aggiungi timestamp per evitare cache
        const response = await fetch(`${resolveAssetPath('version.json')}?t=${Date.now()}`);
        
        // Se la risposta non è ok, non provare a parsare JSON
        if (!response.ok) {
          console.warn('Version file not found or server error');
          return;
        }
        
        const data = await response.json();
        
        // Salva la versione corrente se non esiste
        const storedVersion = localStorage.getItem('app_version');
        if (!storedVersion) {
          localStorage.setItem('app_version', data.version);
          return;
        }
        
        // Controlla se la versione è cambiata
        if (data.version !== storedVersion) {
          setNeedsRefresh(true);
          setVersionData(data); // Salva i dati completi per il banner
        }
      } catch (error) {
        console.warn('Version check failed:', error.message);
        // Non blocchiamo l'app per errori di versioning
      }
    };

    // Check immediato
    checkVersion();
    
    // Check ogni 5 minuti
    const interval = setInterval(checkVersion, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [isDevelopment]);

  if (needsRefresh) {
    // Estrai release notes se disponibili
    const hasReleaseNotes = versionData?.releaseNotes && Array.isArray(versionData.releaseNotes) && versionData.releaseNotes.length > 0;
    const versionName = versionData?.versionName || 'Nuova Versione';
    const versionHistory = Array.isArray(versionData?.versionHistory) ? versionData.versionHistory : [];
    const hasHistory = versionHistory.length > 0;
    const latestChangelogDate = formatReleaseDate(versionData?.releaseDate);

    return (
      <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
        <div className={`p-6 rounded-lg w-full max-h-[90vh] overflow-y-auto ${
          hasHistory ? 'max-w-2xl' : 'max-w-lg'
        } ${isDark ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'}`}>
          {/* Header */}
          <div className="text-center mb-4">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 dark:bg-blue-900 mb-3">
              <svg className="w-8 h-8 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold mb-2">
              Aggiornamento Disponibile
            </h2>
            {versionData?.versionName && (
              <p className={`text-lg font-semibold ${
                isDark ? 'text-blue-400' : 'text-blue-600'
              }`}>
                {versionName}
                {latestChangelogDate && (
                  <span className={`block text-sm font-normal mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    {latestChangelogDate}
                  </span>
                )}
              </p>
            )}
          </div>

          {/* Release Notes o Messaggio Generico */}
          {hasReleaseNotes ? (
            <div className="mb-6">
              <h3 className={`text-sm font-semibold mb-3 uppercase tracking-wide ${
                isDark ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Novità in questa versione:
              </h3>
              <ul className="space-y-2 text-left">
                {versionData.releaseNotes.map((note, index) => (
                  <li 
                    key={index}
                    className={`flex items-start gap-2 text-sm ${
                      isDark ? 'text-gray-300' : 'text-gray-700'
                    }`}
                  >
                    <span className={`inline-block mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                      isDark ? 'bg-blue-400' : 'bg-blue-600'
                    }`} />
                    <span className="flex-1">{note}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p className={`mb-6 text-center ${
              isDark ? 'text-gray-300' : 'text-gray-700'
            }`}>
              È disponibile una nuova versione dell'applicazione. 
              Aggiorna ora per avere tutte le ultime funzionalità e correzioni.
            </p>
          )}

          {/* Versioni precedenti (CHANGELOG) */}
          {hasHistory && (
            <details
              className={`mb-5 rounded-lg border text-left ${
                isDark ? 'border-gray-600 bg-gray-900/40' : 'border-gray-200 bg-gray-50'
              }`}
            >
              <summary
                className={`cursor-pointer select-none px-4 py-3 text-sm font-semibold list-none [&::-webkit-details-marker]:hidden ${
                  isDark ? 'text-gray-200 hover:bg-gray-700/50' : 'text-gray-800 hover:bg-gray-100'
                } rounded-lg`}
              >
                Versioni precedenti ({versionHistory.length})
                <span className={`block text-xs font-normal mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                  Apri per consultare le versioni precedenti e i relativi aggiornamenti
                </span>
              </summary>
              <div className="border-t border-gray-200 dark:border-gray-600 px-4 py-3 space-y-4 max-h-72 overflow-y-auto">
                {versionHistory.map((entry, blockIdx) => (
                  <div
                    key={`${entry.versionName}-${entry.releaseDate}-${blockIdx}`}
                    className={`rounded-md border p-3 ${
                      isDark ? 'border-gray-600 bg-gray-800/80' : 'border-gray-200 bg-white'
                    }`}
                  >
                    <p className={`text-sm font-semibold ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>
                      {entry.versionName}
                      {entry.releaseDate && (
                        <span className={`font-normal ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          {' · '}
                          {formatReleaseDate(entry.releaseDate)}
                        </span>
                      )}
                    </p>
                    {Array.isArray(entry.releaseNotes) && entry.releaseNotes.length > 0 ? (
                      <ul className="mt-2 space-y-1.5 text-left">
                        {entry.releaseNotes.map((note, idx) => (
                          <li
                            key={idx}
                            className={`flex items-start gap-2 text-xs ${
                              isDark ? 'text-gray-300' : 'text-gray-700'
                            }`}
                          >
                            <span
                              className={`mt-1.5 inline-block h-1 w-1 flex-shrink-0 rounded-full ${
                                isDark ? 'bg-blue-400' : 'bg-blue-600'
                              }`}
                            />
                            <span className="flex-1">{note}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className={`mt-2 text-xs italic ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                        Nessun elenco puntato in changelog per questa versione.
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </details>
          )}

          {/* Timestamp build (opzionale) */}
          {versionData?.timestamp && (
            <p className={`text-xs text-center mb-4 ${
              isDark ? 'text-gray-500' : 'text-gray-500'
            }`}>
              Pubblicazione build: {new Date(versionData.timestamp).toLocaleDateString('it-IT', {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </p>
          )}

          {/* Bottone Aggiorna */}
          <button
            onClick={() => {
              localStorage.removeItem('app_version');
              window.location.reload(true);
            }}
            className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-semibold shadow-lg hover:shadow-xl"
          >
            Aggiorna Ora
          </button>
        </div>
      </div>
    );
  }

  return children;
};

export default VersionChecker;